import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';

/**
 * How long the typing pauses before the term reaches the address bar and the API.
 *
 * ⚠️ **A request per keystroke walks into the throttle.** Every authenticated route is 300/min per
 * user (BUG-011), and `GET /api/v1/considerations/` is the route the whole screen lives on: a
 * ten-character name typed twice is twenty requests, and a throttled candidate list is §8's error
 * state on the product's busiest screen. 300ms is also roughly the pause a person makes between
 * words, so the request that goes out is usually the one they meant.
 */
export const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  /** What the address bar says — the source of truth (`candidateFilters.ts`). */
  value: string;
  /** Called with the settled term, once per pause. The screen writes it into the URL. */
  onCommit: (term: string) => void;
}

/**
 * §6.3's *"search by name"*, debounced into the URL.
 *
 * **The input is local state and the term is not.** What the user has typed but not yet paused on
 * belongs to the control; the applied filter belongs to the address bar, because §6.3 requires a
 * filtered list to survive a reload and the back button. That split is the whole of this component,
 * and both directions of it are load-bearing:
 *
 *  * typing → after a pause, `onCommit` writes `?search=` (the screen pushes a history entry, so
 *    Back undoes a search);
 *  * the URL changing underneath — Back, Forward, *Clear filters*, or a link from another screen —
 *    → the input adopts it.
 *
 * ⚠️ Without the second direction the two fight: Back would restore the previous `?search=`, the
 * input would still hold the typed text, and the pending timer would write it straight back — a back
 * button that appears not to work. `committed` is what tells the two apart: it holds the value the
 * URL and this input last agreed on, so a change on either side is attributable.
 */
export function SearchBox({ value, onCommit }: Props) {
  const { t } = useTranslation('candidates');
  const [draft, setDraft] = useState(value);

  /**
   * The last value the URL and this input agreed on — see the docblock. It holds the term **as the
   * URL keeps it**, which is trimmed (`readCandidateFilters`): without that, `"ada "` would commit,
   * read back as `"ada"`, look like a change made elsewhere, and reset the input mid-word — deleting
   * characters the user had already typed after the pause.
   */
  const committed = useRef(value);

  /**
   * `onCommit` in a ref, so the debounce below depends on the typing alone. Given as a prop it is a
   * new function on every parent render, and the list re-renders while a request is in flight: as a
   * dependency it would restart the timer each time and could hold a term back indefinitely.
   */
  const commit = useRef(onCommit);
  useEffect(() => {
    commit.current = onCommit;
  }, [onCommit]);

  // The URL moved on its own (Back, Forward, Clear filters, a link): adopt it and cancel the timer.
  useEffect(() => {
    if (value === committed.current) return;
    committed.current = value;
    setDraft(value);
  }, [value]);

  useEffect(() => {
    const term = draft.trim();
    if (term === committed.current) return;

    const timer = setTimeout(() => {
      committed.current = term;
      commit.current(term);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [draft]);

  return (
    <Field label={t('filters.search')} className="w-full sm:w-64">
      {(fieldProps) => (
        <Input
          {...fieldProps}
          /**
           * `type="search"` rather than `text`: it is what a browser offers a clear affordance and a
           * history for, and what a screen reader announces as a search field. The row is not a
           * `<form>` — there is nothing to submit, because the address bar is the filter.
           */
          type="search"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      )}
    </Field>
  );
}
